import { useEffect, useState } from "react";
import { Flag, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, Spinner, EmptyRow } from "@/components/admin/ui";
import { fmtLap } from "@/pages/MySessions";

export default function AdminLeaderboardTracks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = () =>
    apiClient.get("/admin/leaderboard-tracks")
      .then(({ data }) => { setRows(data); setDrafts(Object.fromEntries(data.map((r) => [r.track_id, r.display_name]))); })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async (track_id, patch) => {
    setSaving((s) => ({ ...s, [track_id]: true }));
    try {
      await apiClient.put(`/admin/leaderboard-tracks/${encodeURIComponent(track_id)}`, patch);
      toast.success("Track updated");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to update track")); }
    finally { setSaving((s) => ({ ...s, [track_id]: false })); }
  };

  if (loading) return <Spinner label="Loading tracks" />;

  return (
    <div>
      <AdminHeader title="Leaderboard Tracks" subtitle={`${rows.length} track${rows.length === 1 ? "" : "s"} detected · rename or hide from the leaderboard`} />
      <TableWrap>
        <thead>
          <Tr>
            <Th>Track ID</Th>
            <Th>Display Name</Th>
            <Th>Recordings</Th>
            <Th>Best Lap</Th>
            <Th>Visible</Th>
            <Th></Th>
          </Tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={6} text="No tracks with recorded lap times yet" />
          ) : rows.map((r) => (
            <Tr key={r.track_id}>
              <Td>
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300 font-mono text-xs" data-testid={`track-id-${r.track_id}`}>{r.track_id}</span>
                </div>
              </Td>
              <Td>
                <input
                  value={drafts[r.track_id] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [r.track_id]: e.target.value }))}
                  className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm w-64 focus:outline-none focus:border-cyan-400/50"
                  placeholder={r.track_id}
                  data-testid={`track-name-${r.track_id}`}
                />
              </Td>
              <Td className="text-slate-400">{r.recordings.toLocaleString()}</Td>
              <Td style={{ color: "#00ff9d" }}>{fmtLap(r.best_lap_ms)}</Td>
              <Td>
                <button
                  onClick={() => save(r.track_id, { visible: !r.visible })}
                  className="chip"
                  style={{
                    background: r.visible ? "rgba(0,255,157,0.14)" : "rgba(148,163,184,0.14)",
                    border: `1px solid ${r.visible ? "rgba(0,255,157,0.45)" : "rgba(148,163,184,0.45)"}`,
                    color: r.visible ? "#00ff9d" : "#94a3b8",
                  }}
                  data-testid={`track-visible-${r.track_id}`}
                >
                  {r.visible ? <><Eye className="w-3.5 h-3.5" /> Visible</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}
                </button>
              </Td>
              <Td>
                <button
                  onClick={() => save(r.track_id, { display_name: drafts[r.track_id] })}
                  disabled={saving[r.track_id] || (drafts[r.track_id] ?? "").trim() === r.display_name}
                  className="chip disabled:opacity-40"
                  style={{ background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.45)", color: "#00e5ff" }}
                  data-testid={`track-save-${r.track_id}`}
                >
                  <Save className="w-3.5 h-3.5" /> Save Name
                </button>
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>
    </div>
  );
}
